import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  filterWorkspaceTourSteps,
  WORKSPACE_TOUR_NARROW_MAX_WIDTH,
  type WorkspaceTourPrepare,
  type WorkspaceTourSection,
  type WorkspaceTourStep,
} from './workspaceTourConfig';
import { hasFinishedWorkspaceTour, markWorkspaceTourFinished } from './workspaceTourStorage';

type SectionType = WorkspaceTourSection | 'admin';

type Options = {
  userId: string | null | undefined;
  eligible: boolean;
  accessReady: boolean;
  includeAi: boolean;
  activeSection: SectionType;
  onSectionChange: (section: WorkspaceTourSection) => void;
  onPrepare?: (prepare: WorkspaceTourPrepare) => void;
};

const TARGET_WAIT_MS = 2500;
const POLL_MS = 50;

function isNarrowViewport(): boolean {
  return window.innerWidth <= WORKSPACE_TOUR_NARROW_MAX_WIDTH;
}

function waitForTarget(target: string, timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      if (document.querySelector(`[data-tour="${target}"]`)) {
        resolve(true);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        resolve(false);
        return;
      }
      window.setTimeout(tick, POLL_MS);
    };
    tick();
  });
}

export function useWorkspaceTour({
  userId,
  eligible,
  accessReady,
  includeAi,
  activeSection,
  onSectionChange,
  onPrepare,
}: Options) {
  const steps = useMemo(() => filterWorkspaceTourSteps(includeAi), [includeAi]);
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepReady, setStepReady] = useState(false);
  const advancingRef = useRef(false);
  const autoStartedRef = useRef(false);
  const onSectionChangeRef = useRef(onSectionChange);
  const onPrepareRef = useRef(onPrepare);
  onSectionChangeRef.current = onSectionChange;
  onPrepareRef.current = onPrepare;

  const currentStep: WorkspaceTourStep | null = active ? steps[stepIndex] ?? null : null;

  const finish = useCallback((status: 'completed' | 'skipped') => {
    if (userId) markWorkspaceTourFinished(userId, status);
    setActive(false);
    setStepIndex(0);
    setStepReady(false);
    onSectionChangeRef.current('dashboard');
  }, [userId]);

  const goToStep = useCallback(async (index: number, currentSection: SectionType) => {
    const step = steps[index];
    if (!step) {
      finish('completed');
      return;
    }
    advancingRef.current = true;
    setStepReady(false);
    setStepIndex(index);
    if (currentSection !== step.section) {
      onSectionChangeRef.current(step.section);
    }
    if (step.prepare) onPrepareRef.current?.(step.prepare);
    await waitForTarget(step.target, TARGET_WAIT_MS);
    setStepReady(true);
    advancingRef.current = false;
  }, [finish, steps]);

  const start = useCallback(() => {
    if (!eligible || !userId || steps.length === 0) return;
    if (isNarrowViewport()) return;
    setActive(true);
    setStepIndex(0);
    void goToStep(0, 'dashboard');
  }, [eligible, goToStep, steps.length, userId]);

  useEffect(() => {
    if (!accessReady || !eligible || !userId) return;
    if (hasFinishedWorkspaceTour(userId)) return;
    if (isNarrowViewport()) return;
    if (autoStartedRef.current) return;
    autoStartedRef.current = true;
    const timer = window.setTimeout(() => start(), 350);
    return () => window.clearTimeout(timer);
  }, [accessReady, eligible, start, userId]);

  useEffect(() => {
    if (!active || !currentStep || advancingRef.current) return;
    if (activeSection !== currentStep.section) {
      onSectionChange(currentStep.section);
    }
  }, [active, activeSection, currentStep, onSectionChange]);

  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        finish('skipped');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, finish]);

  const next = useCallback(() => {
    if (stepIndex >= steps.length - 1) {
      finish('completed');
      return;
    }
    void goToStep(stepIndex + 1, activeSection);
  }, [activeSection, finish, goToStep, stepIndex, steps.length]);

  const back = useCallback(() => {
    if (stepIndex <= 0) return;
    void goToStep(stepIndex - 1, activeSection);
  }, [activeSection, goToStep, stepIndex]);

  return {
    active: active && Boolean(currentStep) && stepReady,
    currentStep,
    stepIndex,
    totalSteps: steps.length,
    start,
    next,
    back,
    skip: () => finish('skipped'),
  };
}
